/* eslint-disable camelcase */
import * as Constants from 'client/util/Constants';
import ItemType from 'client/util/Constants/ItemType';
import PolicyApi from 'client/api/v2/PolicyApi';
import PolicyTemplateApi from 'client/api/v2/PolicyTemplateApi';

const { DeviceItemType, ConfigurationItemType } = ItemType;
const deviceItem = new DeviceItemType();
const configurationItem = new ConfigurationItemType();

const { ItemTypeRoutes } = Constants;
const { linux, macOS, windows } = Constants.default;

export default {

  data() {
    return {
      policyTemplateNameToId: {},
      policyTemplateNames: [
        'mdm_darwin',
        'custom_mdm_profile_darwin',
        'removable_storage_darwin',
        'disable_usb_storage_linux',
        'removablestorage_windows',
        'bitlocker_fde_windows',
        'filevault-2_darwin',
        'local_firewall_darwin',
        'windowsfirewall_windows',
        'lock_screen_darwin',
        'lock_screen_windows',
        'system_preferences_panes_darwin',
        'restrict_control_panel_panes_windows',
        'block_macos_big_sur_install_darwin',
      ],
      remainingPolicyTemplates: {},
    };
  },

  methods: {
    initRemainingPolicies() {
      /*
       initRemainingPolicies determines which policies the user
       already has, not showing those as recommended policies
      */
      const policyTemplatesMap = {};
      return Promise.all([
        PolicyApi.findPoliciesByName(this.policyTemplateNames),
        PolicyTemplateApi.findPolicyTemplatesByName(this.policyTemplateNames),
      ])
        .then(([policies, policyTemplates]) => {
          policyTemplates.results.forEach((policyTemplate) => {
            policyTemplatesMap[policyTemplate.name] = policyTemplate.id;
          });
          this.policyTemplateNameToId = policyTemplatesMap;
          const remainingPolicyTemplatesMap = { ...policyTemplatesMap };
          policies.results.forEach((policy) => {
            delete remainingPolicyTemplatesMap[policy.template.name];
          });
          this.remainingPolicyTemplates = remainingPolicyTemplatesMap;
          if (Object.keys(this.remainingPolicyTemplates).length === 0) {
            this.$emit('no-policies-left');
          }
        });
    },

    refreshRemainingPolicies() {
      return PolicyApi.findPoliciesByName(this.policyTemplateNames)
        .then((policies) => {
          const remainingPolicyTemplatesMap = { ...this.policyTemplateNameToId };
          policies.results.forEach((policy) => {
            delete remainingPolicyTemplatesMap[policy.template.name];
          });
          this.remainingPolicyTemplates = remainingPolicyTemplatesMap;
        });
    },

    policyUrl(policyId) {
      const policyName = this.policyTemplatesKeys
        .find(key => this.policyTemplateNameToId[key] === policyId);
      if (!policyName) return false;
      if (policyName.includes('linux')) return `${this.baseLinuxURL}/${policyId}`;
      if (policyName.includes('darwin')) return `${this.baseMacURL}/${policyId}`;
      return `${this.baseWindowsURL}/${policyId}`;
    },
  },

  computed: {
    baseURL() {
      return `${ItemTypeRoutes[this.itemType]}/configure`;
    },

    baseLinuxURL() {
      return `${this.baseURL}/linux`;
    },

    baseMacURL() {
      return `${this.baseURL}/darwin`;
    },

    baseWindowsURL() {
      return `${this.baseURL}/windows`;
    },

    policyData() {
      return [
        {
          title: `MDM Enrollment ${configurationItem.title}`,
          description: `Remotely enroll ${deviceItem.labelPlural} in JumpCloud's MDM across your organization.`,
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId.mdm_darwin),
              remaining: this.remainingPolicyTemplates.mdm_darwin,
            },
          ],
          showItem: this.showMdmEnrollment,
        },
        {
          title: 'Block macOS Big Sur Installation',
          description: 'Prevents the macOS Big Sur installation application and macOS Big Sur Beta installation application from launching.',
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId.block_macos_big_sur_install_darwin),
              remaining: this.remainingPolicyTemplates.block_macos_big_sur_install_darwin,
            },
          ],
          showItem: this.showBigSurInstallation,
        },
        {
          title: `MDM Custom ${configurationItem.title}`,
          description: `Distribute custom MDM ${configurationItem.label} profile using this policy.`,
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId.custom_mdm_profile_darwin),
              remaining: this.remainingPolicyTemplates.custom_mdm_profile_darwin,
            },
          ],
          showItem: this.showMdmCustom,
        },
        {
          title: 'Disable USB mass storage devices',
          description: `Prevents mounting of removable storage ${deviceItem.plural} this includes USB mass-storage ${deviceItem.plural} and SD flash cards.`,
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId.removable_storage_darwin),
              remaining: this.remainingPolicyTemplates.removable_storage_darwin,
            },
            {
              title: windows,
              href: this.policyUrl(this.policyTemplateNameToId.removablestorage_windows),
              remaining: this.remainingPolicyTemplates.removablestorage_windows,
            },
            {
              title: linux,
              href: this.policyUrl(this.policyTemplateNameToId.disable_usb_storage_linux),
              remaining: this.remainingPolicyTemplates.disable_usb_storage_linux,
            },
          ],
          showItem: this.showDisableUsb,
        },
        {
          title: 'Full Disk Encryption',
          description: `Provides an additional level of protection by preventing unauthorized
          parties from viewing any data stored on the device in the event of accidental loss or theft.`,
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId['filevault-2_darwin']),
              remaining: this.remainingPolicyTemplates['filevault-2_darwin'],
            },
            {
              title: windows,
              href: this.policyUrl(this.policyTemplateNameToId.bitlocker_fde_windows),
              remaining: this.remainingPolicyTemplates.bitlocker_fde_windows,
            },
          ],
          showItem: this.showDiscEncryption,
        },
        {
          title: 'Firewall Controls',
          description: 'Protect your users from unsolicited incoming traffic, as well as, certain types of outgoing traffic.',
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId.local_firewall_darwin),
              remaining: this.remainingPolicyTemplates.local_firewall_darwin,
            },
            {
              title: windows,
              href: this.policyUrl(this.policyTemplateNameToId.windowsfirewall_windows),
              remaining: this.remainingPolicyTemplates.windowsfirewall_windows,
            },
          ],
          showItem: this.showFirewall,
        },
        {
          title: 'Lock Screen',
          description: `Allows admins to configure the amount of time (in seconds) that a ${deviceItem.label}
          can remain idle before the screensaver engages, locking the ${deviceItem.plural} down.`,
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId.lock_screen_darwin),
              remaining: this.remainingPolicyTemplates.lock_screen_darwin,
            },
            {
              title: windows,
              href: this.policyUrl(this.policyTemplateNameToId.lock_screen_windows),
              remaining: this.remainingPolicyTemplates.lock_screen_windows,
            },
          ],
          showItem: this.showLockScreen,
        },
        {
          title: `Lock Down Control Panel & ${deviceItem.title} Preferences`,
          description: `Selectively disable certain control panel or ${deviceItem.label} preference
          menu options from your users.`,
          availability: [
            {
              title: macOS,
              href: this.policyUrl(this.policyTemplateNameToId.system_preferences_panes_darwin),
              remaining: this.remainingPolicyTemplates.system_preferences_panes_darwin,
            },
            {
              title: windows,
              href: this.policyUrl(this.policyTemplateNameToId
                .restrict_control_panel_panes_windows),
              remaining: this.remainingPolicyTemplates.restrict_control_panel_panes_windows,
            },
          ],
          showItem: this.showLockDownControls,
        },
      ];
    },

    policyDataFiltered() {
      const filteredByAvailability = this.policyData.map((policy) => {
        const newPolicy = { ...policy };
        newPolicy.availability = policy.availability.filter(obj => obj.remaining);
        return newPolicy;
      });
      return filteredByAvailability.filter(policy => policy.showItem && policy.availability.length)
        .concat([{
          description: `You can explore more ${configurationItem.labelPlural} in the Recommended Tab.`,
          seeAllCard: true,
          title: 'seeAllCard',
        }]);
    },

    policyTemplatesKeys() {
      return Object.keys(this.policyTemplateNameToId);
    },

    showBigSurInstallation() {
      return this.remainingPolicyTemplates.block_macos_big_sur_install_darwin;
    },

    showDisableUsb() {
      const {
        disable_usb_storage_linux,
        removable_storage_darwin,
        removablestorage_windows,
      } = this.remainingPolicyTemplates;
      return disable_usb_storage_linux || removable_storage_darwin || removablestorage_windows;
    },

    showDiscEncryption() {
      const { bitlocker_fde_windows } = this.remainingPolicyTemplates;
      const filevault_darwin = this.remainingPolicyTemplates['filevault-2_darwin'];
      return bitlocker_fde_windows || filevault_darwin;
    },

    showFirewall() {
      const { local_firewall_darwin, windowsfirewall_windows } = this.remainingPolicyTemplates;
      return local_firewall_darwin || windowsfirewall_windows;
    },

    showLockScreen() {
      const { lock_screen_darwin, lock_screen_windows } = this.remainingPolicyTemplates;
      return lock_screen_darwin || lock_screen_windows;
    },

    showLockDownControls() {
      const {
        system_preferences_panes_darwin,
        restrict_control_panel_panes_windows,
      } = this.remainingPolicyTemplates;
      return system_preferences_panes_darwin || restrict_control_panel_panes_windows;
    },

    showMdmEnrollment() {
      return this.remainingPolicyTemplates.mdm_darwin;
    },

    showMdmCustom() {
      return this.remainingPolicyTemplates.custom_mdm_profile_darwin;
    },
  },
};
